import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { privateAlbumService } from '@/services/privateAlbumService';
import { toast } from '@/hooks/use-toast';

interface PrivateAlbumCreationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAlbumCreated: () => void;
}

const PrivateAlbumCreationDialog = ({ 
  open, 
  onOpenChange, 
  onAlbumCreated 
}: PrivateAlbumCreationDialogProps) => {
  const { user } = useAuth();
  const [albumName, setAlbumName] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!user || !albumName.trim()) return;

    try {
      setCreating(true);
      await privateAlbumService.createAlbum(user.id, albumName.trim());
      
      toast({
        title: "Album Created",
        description: `"${albumName}" has been created successfully.`
      });
      
      setAlbumName('');
      onAlbumCreated();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create album",
        variant: "destructive"
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Private Album</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="albumName">Album Name</Label>
            <Input
              id="albumName"
              value={albumName}
              onChange={(e) => setAlbumName(e.target.value)}
              placeholder="Enter album name..."
              maxLength={50}
            />
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!albumName.trim() || creating}
            >
              {creating ? 'Creating...' : 'Create Album'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrivateAlbumCreationDialog;